const AgreementContent = () => {
  return (
    <div className="max-w-none text-dark-grey dark:text-white [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-6 [&_h2]:mb-3 [&_p]:leading-6 [&_p]:mb-4">
      <h1>Gradient Nexus Software License Agreement</h1>

      <h2>Introduction</h2>
      <p>
        This Software License Agreement ("Agreement") is entered into by and
        between Gradient Comfort, Inc., a Delaware corporation ("Gradient"), and
        the customer identified on the corresponding Order Form ("Customer"),
        effective as of the date of Customer's acceptance (the "Effective
        Date").
      </p>

      <h2>1. License Grant</h2>
      <p>
        Gradient grants Customer a non-exclusive, non-transferable, limited
        license to access and use the Gradient Nexus platform ("Nexus" or
        "Software") for internal business purposes related to managing and
        optimizing Gradient heat pump units installed at Customer's properties.
        The license granted hereunder is limited to Gradient units identified by
        serial number on the applicable Order Form or subsequently registered
        through Nexus ("Licensed Units"). This license does not convey any
        ownership rights or title to the Software or its associated intellectual
        property. Upon sale or transfer of the real property in which Licensed
        Units are installed, Customer may transfer this license to the new
        property owner, provided that: (a) Customer notifies Gradient in writing;
        (b) the license remains active and transferee accepts the then-current
        terms; and (c) Customer's access terminates upon transfer. In order to
        extend the license beyond the active Term, the transferee must contract
        directly with Gradient before the expiration of the active Term. Customer
        shall not, and shall not permit any third party to: (i) reverse engineer,
        decompile, or disassemble the
        Software; (ii) access the Software to build a competitive product; or
        (iii) use the Software for benchmarking purposes without Gradient's prior
        written consent. This restriction applies to the maximum extent permitted
        by applicable law.
      </p>

      <h2>2. Fees and Payment</h2>
      <p>
        2.1 Per-Unit Annual Fee: Customer shall pay a per-unit annual fee for
        each Gradient unit actively connected to and managed via Nexus.
      </p>
      <p>
        2.2 Billing and Payment Terms: Unless otherwise agreed in writing, fees
        are invoiced annually in advance for unit usage. Payment is due within
        thirty (30) days of invoice date. Late payments may accrue interest at
        1.5% per month or the maximum rate permitted by law.
      </p>
      <p>
        2.3 Non-Payment: If Customer fails to pay undisputed amounts within
        thirty (30) days after written notice, Gradient shall have the right (but
        not the obligation) to suspend access after providing an additional five
        (5) days' notice of intent to suspend. Suspension does not terminate this
        Agreement, relieve payment obligations, or waive Gradient's right to
        pursue other remedies. Gradient shall restore access within five (5)
        business days of receiving full payment.
      </p>

      <h2>3. Service Availability and Support</h2>
      <p>
        3.1 Service Level Commitment: Gradient shall use commercially reasonable
        efforts to ensure Uptime and responsiveness. In the event of downtime,
        Gradient's response time commitment will range between 24 to 72 hours,
        depending on severity level. "Uptime" means the percentage of time during
        a calendar month that the Software is available and accessible for use by
        Customer, excluding scheduled maintenance windows, force majeure events,
        and downtime caused by Customer's systems, third-party services, or
        internet connectivity.
      </p>
      <p>
        3.2 Customer Support: The Software will be supported by Gradient's
        Customer Service Team, available via published contact channels (email or
        portal).
      </p>
      <p>
        3.3 Maintenance and Updates: Gradient may perform routine maintenance or
        deploy updates to enhance security, reliability, or features. Such
        updates may result in temporary service interruptions.
      </p>
      <p>
        3.4 No Uptime Guarantee: Gradient does not guarantee uninterrupted access
        to Nexus and shall not be liable for service interruptions due to
        maintenance, external connectivity issues, or third-party dependencies.
      </p>
      <p>
        3.5 Platform Continuity: Gradient shall maintain the Nexus platform for a
        minimum of 1 year from the Effective Date. Gradient shall provide at
        least twelve (12) months' advance written notice before discontinuing the
        Nexus platform or ceasing to provide security updates.
      </p>

      <h2>4. Nature of the Software</h2>
      <p>
        Customer acknowledges and agrees that Nexus is intended for monitoring,
        management, analytics, configuration, and administrative use. While Nexus
        may be used to transmit settings or configurations to Gradient units, the
        units function independently of the Software, and any downtime or
        unavailability of Nexus shall not affect the operation of physical
        Gradient heat pump units or be deemed a service failure of Gradient's
        HVAC products.
      </p>
      <p>
        Customer is solely responsible for any commands, settings, or
        configurations it directs Nexus to transmit to Gradient units, and
        Gradient shall have no liability for any consequences resulting from
        Customer's use of such features. Power and energy data provided by the
        Software are estimates with an accuracy of approximately ±10%, are
        directional in nature, and are not utility- or revenue-grade. Such data
        is for informational purposes only and is not suitable for billing,
        settlement, or regulatory use. Gradient disclaims all liability arising
        from reliance on such data.
      </p>

      <h2>5. Ownership and Intellectual Property</h2>
      <p>
        Gradient retains all rights, title, ownership and interest in and to the
        Software, including all intellectual property, documentation, user
        interfaces, and updates. Customer acknowledges that the Software contains
        proprietary algorithms, trade secrets, and confidential information of
        significant value to Gradient. Customer shall not copy, modify, reverse
        engineer, sublicense, or otherwise transfer any rights to the Software.
      </p>

      <h2>6. Data Handling and Privacy</h2>
      <p>6.1 Definitions:</p>
      <p>
        "Aggregate Data" means aggregated, anonymized, or de-identified Device
        Data.
      </p>
      <p>
        "Customer Data" means any data, content, or information that Customer or
        its authorized users upload, submit, store, or transmit through or to the
        Software, including account information, property details, tenant or
        occupant information, user preferences, and any configurations or settings
        applied by Customer. Customer Data excludes Device Data, Aggregate Data
        and Usage Data.
      </p>
      <p>
        "Device Data" means operational telemetry from Gradient devices, including
        temperature data, energy usage, runtime hours, fault codes, and similar
        metrics, which Gradient may collect and process.
      </p>
      <p>
        "Usage Data" means data generated by or collected through Customer's or
        its authorized users' interaction with the Software, including login
        activity, feature usage, session duration, user interface interactions,
        system logs, and performance metrics related to Customer's use of the
        Software. Usage Data does not include the content of Customer Data or
        Device Data.
      </p>
      <p>
        6.2 Ownership and License: Customer retains ownership of Device Data
        specific to its properties, users, and devices. Gradient retains
        ownership of Aggregate Data. Gradient may use such Aggregate Data for
        analytics, product improvement, commercial purposes (including licensing
        or sale to third parties), and research.
      </p>
      <p>
        6.3 Data Protection: Gradient will handle all data, including Customer
        Data, in accordance with applicable privacy laws and its published
        Privacy Policy. Gradient shall implement industry-standard security
        measures to protect Customer Data and notify Customer of any suspected
        data breach impacting Customer Data without unreasonable delay. Upon
        termination, Gradient shall securely delete or return all Customer Data
        within thirty (30) days.
      </p>
      <p>
        6.4 Third-Party Services: If Customer connects Nexus with third-party
        systems, Customer is responsible for compliance with those services'
        terms and policies.
      </p>

      <h2>7. Warranty Disclaimer</h2>
      <p>
        EXCEPT AS EXPRESSLY PROVIDED IN SECTION 8, THE SOFTWARE IS PROVIDED "AS
        IS." GRADIENT DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        THIS DISCLAIMER DOES NOT LIMIT GRADIENT'S EXPRESS OBLIGATIONS UNDER
        SECTION 3 OR INDEMNIFICATION DUTIES UNDER SECTION 8. THIS DISCLAIMER
        APPLIES SOLELY TO THE SOFTWARE AND DOES NOT MODIFY, LIMIT, OR SUPERSEDE
        ANY WARRANTY PROVIDED UNDER A SEPARATE AGREEMENT GOVERNING THE PURCHASE OR
        INSTALLATION OF GRADIENT HARDWARE.
      </p>

      <h2>8. Indemnification and Limitation of Liability</h2>
      <p>
        Each party shall indemnify, defend, and hold harmless the other party
        from third-party claims arising out of or relating to the Software,
        including claims arising from: (a) breach of this Agreement, (b) negligent
        or wrongful acts in connection with the Software, and (c) violation of
        applicable laws in connection with the Software. Gradient shall
        additionally indemnify Customer against IP infringement claims related to
        the Software.
      </p>
      <p>
        EXCEPT IN THE CASE OF GROSS NEGLIGENCE, WILLFUL MISCONDUCT, BREACH OF
        CONFIDENTIALITY, DATA BREACHES INVOLVING PERSONAL INFORMATION, OR IP
        INFRINGEMENT, IN NO EVENT SHALL GRADIENT BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF
        PROFITS, DATA, OR GOODWILL. GRADIENT'S TOTAL LIABILITY UNDER THIS
        AGREEMENT SHALL NOT EXCEED THE TOTAL FEES PAID BY CUSTOMER IN THE TWELVE
        (12) MONTHS PRECEDING THE CLAIM. THIS LIMITATION OF LIABILITY APPLIES
        SOLELY TO CLAIMS ARISING FROM THE SOFTWARE AND DOES NOT LIMIT EITHER
        PARTY'S LIABILITY FOR CLAIMS ARISING FROM DEFECTS IN GRADIENT HARDWARE,
        WHICH ARE GOVERNED BY THE APPLICABLE HARDWARE PURCHASE OR INSTALLATION
        AGREEMENT.
      </p>

      <h2>9. Term and Termination</h2>
      <p>
        9.1 Term: This Agreement commences on the Effective Date and remains in
        effect for an initial term stated in the PO unless terminated earlier
        (the "Initial Term"). Thereafter, this Agreement shall automatically renew
        for the same period terms (the "Renewal Term") at Gradient's then-current
        rates, unless either party provides written notice of non-renewal at least
        thirty (30) days prior to the end of the then-current term. The Initial
        Term and the Renewal Term collectively, are the "Term."
      </p>
      <p>
        9.2 Termination for Cause: Either party may terminate with thirty (30)
        days' notice if the other materially breaches and fails to cure within
        thirty (30) days of receiving written notice of such breach.
      </p>
      <p>9.3 Reserved.</p>
      <p>
        9.4 Effect: Upon termination, Customer's access ceases immediately and all
        outstanding fees immediately become due. For the avoidance of doubt,
        termination of this Agreement does not affect the independent operation of
        Gradient hardware units, which will continue to provide heating and
        cooling functionality without access to Nexus.
      </p>

      <h2>10. Confidentiality</h2>
      <p>
        Each party shall protect Confidential Information using the same degree of
        care used for its own confidential information, but no less than
        reasonable care. These obligations survive termination for five (5) years,
        except for trade secrets, which shall be protected indefinitely or until
        publicly known through no breach by the receiving party.
      </p>

      <h2>11. Governing Law</h2>
      <p>
        This Agreement is governed by the laws of the State of California, without
        regard to conflict-of-law principles. Disputes shall be brought in the
        state or federal courts located in San Francisco County, California.
      </p>

      <h2>12. Assignment</h2>
      <p>
        Neither party may assign this Agreement without prior written consent of
        the other party, except that Gradient may assign to an affiliate or in
        connection with a merger, acquisition, or sale of all or substantially all
        of its assets. Notwithstanding the foregoing, transfers permitted under
        Section 1 shall not require prior written consent.
      </p>

      <h2>13. Force Majeure</h2>
      <p>
        Neither party shall be liable for failure to perform due to causes beyond
        reasonable control, including acts of God, government actions, terrorism,
        pandemics, or internet failures, provided the affected party: (i) provides
        prompt notice; (ii) uses reasonable efforts to mitigate; and (iii) resumes
        performance when possible. This clause does not excuse payment
        obligations.
      </p>

      <h2>14. Entire Agreement</h2>
      <p>
        This Agreement (including associated Order Forms) constitutes the entire
        understanding between the parties and supersedes all prior agreements.
      </p>
    </div>
  );
};

export default AgreementContent;
